import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, Loader2, ShoppingBag } from "lucide-react";
import { clearStoredCart } from "./Storefront";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResult({ mode, onStore, onHome }) {
  const [state, setState] = useState(mode === "success" ? "confirming" : "canceled");
  const ran = useRef(false);

  useEffect(() => {
    if (mode !== "success" || ran.current) return;
    ran.current = true;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        if (res.status === 404) {
          setState("error");
          return;
        }
        const body = await res.json();
        if (res.ok && body.payment_status === "paid") {
          clearStoredCart();
          setState("paid");
          return;
        }
        if (res.ok && (body.payment_status === "failed" || body.payment_status === "expired")) {
          setState("failed");
          return;
        }
      } catch (err) {
        console.debug("status poll retry", err);
      }
      if (tries < 20) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [mode]);

  const wrap = (children, testId) => (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid={testId}>
      <div className="bg-white rounded-3xl border border-[#FCE6E9] shadow-[0_20px_50px_-20px_rgba(248,200,220,0.7)] p-10 max-w-md w-full text-center dark:bg-[#273244] dark:border-[#374457]">
        {children}
        <div className="flex gap-2 justify-center mt-8">
          <button
            onClick={onStore}
            data-testid="payment-continue-shopping-btn"
            className="flex items-center gap-1.5 px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#f3b4cd] rounded-full text-xs font-bold text-[#374151] transition-all shadow-sm"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Continue Shopping
          </button>
          <button
            onClick={onHome}
            data-testid="payment-home-btn"
            className="px-5 py-2.5 bg-white hover:bg-[#FFF9F2] border border-[#FCE6E9] rounded-full text-xs font-bold text-[#6B7280] transition-colors dark:bg-[#232D3D] dark:border-[#374457] dark:text-slate-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  if (state === "confirming")
    return wrap(
      <>
        <Loader2 className="w-12 h-12 text-[#B3E0F2] mx-auto animate-spin" />
        <h1 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mt-4">Confirming your payment…</h1>
        <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-2">This usually takes a few seconds.</p>
      </>,
      "payment-confirming"
    );

  if (state === "paid")
    return wrap(
      <>
        <CheckCircle2 className="w-12 h-12 text-[#7CC98F] mx-auto" />
        <h1 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mt-4" data-testid="payment-success-title">Order confirmed!</h1>
        <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-2">
          Thank you — your payment went through. Facility purchases are added to the monthly statement ledger automatically, and every order is listed under My Orders.
        </p>
      </>,
      "payment-success"
    );

  if (state === "canceled")
    return wrap(
      <>
        <XCircle className="w-12 h-12 text-[#F8C8DC] mx-auto" />
        <h1 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mt-4" data-testid="payment-cancel-title">Checkout canceled</h1>
        <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-2">No charge was made. Your cart is saved whenever you're ready.</p>
      </>,
      "payment-cancel"
    );

  return wrap(
    <>
      <XCircle className="w-12 h-12 text-[#B04A6B] mx-auto" />
      <h1 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mt-4" data-testid="payment-error-title">
        {state === "timeout" ? "Still processing" : "Something went wrong"}
      </h1>
      <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-2">
        {state === "timeout"
          ? "Your payment is taking longer than usual to confirm. Check My Orders in the store — it will update as soon as Stripe confirms."
          : "We couldn't confirm this payment. If you were charged, it will appear under My Orders shortly."}
      </p>
    </>,
    "payment-error"
  );
}
